import type { ReactNode } from 'react';
import { PAGER_WORDS } from './list-page.js';

/** A press of the pager: the list works out the page it leads to (`pageAfter`, from the page asked last, so two quick presses go two pages). */
export type PageMove = 'first' | 'prev' | 'next' | 'last';

/** A PAGE BUTTON: an icon with its words as its name and hover; unavailable at the ends, never removed, so the focus stays on it. */
function PageButton(p: { testid: string; words: string; off: boolean; onGo: () => void; children: ReactNode }) {
  return (
    <button type="button" className="lp-pagebtn" aria-label={p.words} title={p.words} aria-disabled={p.off ? true : undefined} data-testid={p.testid} onClick={p.off ? undefined : p.onGo}>
      <svg className="lp-ic" viewBox="0 0 24 24" aria-hidden="true">
        {p.children}
      </svg>
    </button>
  );
}

/** THE PAGER (ported 2026-10-18 from the medrec console's components/list/Pager.tsx, PR #169, the consoles-matched rule): First page,
 *  Previous page, "Page 2 of 39", Next page, Last page, in a group named "Pages". Each button is named by its words (the icon is hidden from
 *  a screen reader); the page words are a polite live region, so a screen reader says "Page 3 of 39" after each move while the focus stays
 *  on the button pressed. Test ids are `<testid>-first`, `-prev`, `-page`, `-next`, `-last` (All unopened's are `un-pager-*`). */
export function Pager({ page, pages, onGo, testid }: { page: number; pages: number; onGo: (to: PageMove) => void; testid: string }) {
  return (
    <span className="lp-pages" role="group" aria-label={PAGER_WORDS.pages}>
      <PageButton testid={`${testid}-first`} words={PAGER_WORDS.firstPage} off={page <= 1} onGo={() => onGo('first')}>
        <path d="m11 17-5-5 5-5M18 17l-5-5 5-5" />
      </PageButton>
      <PageButton testid={`${testid}-prev`} words={PAGER_WORDS.previousPage} off={page <= 1} onGo={() => onGo('prev')}>
        <path d="m15 18-6-6 6-6" />
      </PageButton>
      <span className="lp-page" aria-live="polite" aria-atomic="true" data-testid={`${testid}-page`}>
        {PAGER_WORDS.pageOfPages(page, pages)}
      </span>
      <PageButton testid={`${testid}-next`} words={PAGER_WORDS.nextPage} off={page >= pages} onGo={() => onGo('next')}>
        <path d="m9 18 6-6-6-6" />
      </PageButton>
      <PageButton testid={`${testid}-last`} words={PAGER_WORDS.lastPage} off={page >= pages} onGo={() => onGo('last')}>
        <path d="m13 17 5-5-5-5M6 17l5-5-5-5" />
      </PageButton>
    </span>
  );
}
